import React from 'react';
import './CampaignSurveyAnalytics.css';

const TOP_CONCERN_LABELS = {
  jobs: 'Jobs',
  roads: 'Roads',
  education: 'Education',
  healthcare: 'Healthcare',
  cost_of_living: 'Cost of Living',
  corruption: 'Corruption',
  other: 'Other',
};

const VOTE_INTENT_LABELS = {
  yes: 'Yes',
  no: 'No',
  undecided: 'Undecided',
};

const ACCOUNTABILITY_LABELS = {
  very: 'Very Important',
  somewhat: 'Somewhat Important',
  not: 'Not Important',
};

function CountBars({ entries, fillClass }) {
  if (!entries || entries.length === 0) {
    return <p style={{ color: '#666', fontSize: '0.85rem' }}>No data yet.</p>;
  }
  const max = Math.max(...entries.map((e) => e.count), 1);
  return (
    <div>
      {entries.map((entry) => (
        <div key={entry.name} className="campaign-bar-row">
          <div title={entry.name} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {entry.label || entry.name}
          </div>
          <div className="campaign-bar-track">
            <div
              className={`campaign-bar-fill ${entry.fillClass || fillClass || ''}`}
              style={{ width: `${(entry.count / max) * 100}%` }}
            />
          </div>
          <div className="campaign-bar-value">{entry.count}</div>
        </div>
      ))}
    </div>
  );
}

function ApprovalBars({ approvalBreakdown }) {
  const ratings = [1, 2, 3, 4, 5];
  const counts = ratings.map((r) => approvalBreakdown?.[String(r)] || 0);
  const max = Math.max(...counts, 1);
  return (
    <div>
      {ratings.map((r, idx) => (
        <div key={r} className="campaign-bar-row">
          <div>{r} / 5</div>
          <div className="campaign-bar-track">
            <div
              className="campaign-bar-fill campaign-bar-fill-rating"
              style={{ width: `${(counts[idx] / max) * 100}%` }}
            />
          </div>
          <div className="campaign-bar-value">{counts[idx]}</div>
        </div>
      ))}
    </div>
  );
}

function entriesFrom(obj, labelMap = {}) {
  if (!obj) return [];
  return Object.entries(obj)
    .map(([name, count]) => ({ name, count, label: labelMap[name] || name.replace(/_/g, ' ') }))
    .sort((a, b) => b.count - a.count);
}

export default function CampaignSurveyAnalytics({ analytics }) {
  if (!analytics) return null;

  const {
    total = 0,
    avg_approval_score: avgApproval = 0,
    approval_breakdown: approvalBreakdown = {},
    top_concern_breakdown: topConcernBreakdown = {},
    accountability_weight_breakdown: accountabilityBreakdown = {},
    vote_intent_breakdown: voteIntentBreakdown = {},
    outcome_breakdown: outcomeBreakdown = {},
    free_text_count: freeTextCount = 0,
  } = analytics;

  const concernEntries = entriesFrom(topConcernBreakdown, TOP_CONCERN_LABELS);
  const accountabilityEntries = entriesFrom(accountabilityBreakdown, ACCOUNTABILITY_LABELS);
  const voteEntries = entriesFrom(voteIntentBreakdown, VOTE_INTENT_LABELS).map((e) => ({
    ...e,
    fillClass:
      e.name === 'yes' ? 'campaign-bar-fill-yes'
      : e.name === 'no' ? 'campaign-bar-fill-no'
      : 'campaign-bar-fill-undecided',
  }));
  const outcomeEntries = entriesFrom(outcomeBreakdown);

  return (
    <div className="card campaign-survey-analytics">
      <h3>Campaign Survey Analytics</h3>

      <div className="stats-grid">
        <div className="stat">
          <div className="stat-value">{total}</div>
          <div className="stat-label">Total Responses</div>
        </div>
        <div className="stat">
          <div className="stat-value">{avgApproval ? avgApproval.toFixed(2) : '—'}</div>
          <div className="stat-label">Avg Approval (1–5)</div>
        </div>
        <div className="stat">
          <div className="stat-value">{voteIntentBreakdown?.yes || 0}</div>
          <div className="stat-label">Vote Yes</div>
        </div>
        <div className="stat">
          <div className="stat-value">{freeTextCount}</div>
          <div className="stat-label">Free-Text Messages</div>
        </div>
      </div>

      <div className="campaign-section">
        <h4>Vote Intent</h4>
        <CountBars entries={voteEntries} />
      </div>

      <div className="campaign-section">
        <h4>Top Concerns</h4>
        <CountBars entries={concernEntries} />
      </div>

      <div className="campaign-section">
        <h4>Approval Score Distribution</h4>
        <ApprovalBars approvalBreakdown={approvalBreakdown} />
      </div>

      <div className="campaign-section">
        <h4>Accountability Weight (salary cut + war crimes court)</h4>
        <CountBars entries={accountabilityEntries} />
      </div>

      <div className="campaign-section">
        <h4>Call Outcomes</h4>
        <div className="campaign-breakdown-badges">
          {outcomeEntries.length === 0 ? (
            <span style={{ color: '#666', fontSize: '0.85rem' }}>No data yet.</span>
          ) : (
            outcomeEntries.map((entry) => (
              <span key={entry.name} className="badge badge-neutral">
                {entry.label}: {entry.count}
              </span>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
